"use client";

import Link from "next/link";
import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { ChevronDown, Menu, X, Globe, Phone, Mail, ArrowRight } from "lucide-react";
import { FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaLinkedin, FaWhatsapp } from "react-icons/fa";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLanguage } from "../hooks/useLanguage";
import { fetchAvailableLanguages } from "../redux/slices/languageSlice";
import PublicNavigation from "./PublicNavigation";
import Logo from "./Logo";
import config from "../config/config";

const socialIconMap = {
  facebook: FaFacebook,
  twitter: FaTwitter,
  instagram: FaInstagram,
  youtube: FaYoutube,
  linkedin: FaLinkedin,
  whatsapp: FaWhatsapp,
};

const Header = () => {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerData, setHeaderData] = useState(null);
  const [socialMediaData, setSocialMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language, changeLanguage, availableLanguages } = useLanguage();

  // Don't show header on admin/dashboard routes
  if (pathname?.startsWith("/dashboard")) {
    return null;
  }

  // Fetch available languages from database on mount
  useEffect(() => {
    dispatch(fetchAvailableLanguages());
  }, [dispatch]);

  // Fetch header data from API
  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        setLoading(true);

        // Fetch header settings from dedicated header API
        const headerResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/header?language=${language}`);
        const headerData = await headerResponse.json();

        // Fetch social media settings from separate endpoint
        const socialResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/social-media`);
        const socialData = await socialResponse.json();

        if (headerData.success && headerData.data) {
          setHeaderData(headerData.data);
        } else {
          console.warn('No header data from API:', headerData);
        }

        if (socialData.success && socialData.data) {
          setSocialMediaData(socialData.data);
        } else {
          console.warn('No social media data from API:', socialData);
        }

      } catch (error) {
        console.error('Error fetching header data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeaderData();
  }, [language]);

  // Handle scroll effect with debounced top bar auto-hide
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = Math.abs(currentScrollY - lastScrollY);

          // Set scrolled state for styling
          setScrolled(currentScrollY > 100);

          // Only update if significant scroll movement (reduces flicker)
          if (scrollDifference > 20) {
            if (currentScrollY <= 50) {
              // Always show at top
              setTopBarVisible(true);
            } else {
              // Determine scroll direction with threshold
              if (currentScrollY > lastScrollY + 30) {
                setTopBarVisible(false);
              } else if (currentScrollY < lastScrollY - 30) {
                setTopBarVisible(true);
              }
              // Don't change state for small movements
            }

            setLastScrollY(currentScrollY);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] || field.en;
    }
    return '';
  };

  // Get data with proper structure matching API response
  const logo = headerData?.logo;
  const navigationMenu = headerData?.navigationMenu || [];

  // Get social media data for header (only those marked showInHeader: true)
  const socialMediaProfiles = socialMediaData?.profiles || [];
  const headerLimit = socialMediaData?.displaySettings?.headerLimit || 4;
  const headerSocialEnabled = headerData?.socialMedia?.enabled !== false;

  // Filter and sort social media profiles for header
  const allSocialMedia = Array.isArray(socialMediaProfiles)
    ? socialMediaProfiles
      .filter(profile => profile.isActive && profile.showInHeader)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(profile => ({
        id: profile.id,
        platform: profile.platform,
        url: profile.url,
        order: profile.order
      }))
    : [];

  const socialMedia = headerSocialEnabled ? allSocialMedia.slice(0, headerLimit) : [];

  // Note: phone, email, and breakingNews are no longer available since headerSettings was removed
  const phone = null;
  const email = null;
  const breakingNews = null;
  const joinButton = headerData?.joinButton;
  const topBarButton = headerData?.topBarButton || { enabled: false };
  const languageButton = headerData?.languageButton || { enabled: false };

  if (loading) {
    return (
      <header
        className="sticky top-0 left-0 right-0 z-50 h-20"
        style={{
          backgroundColor: 'var(--color-card)',
          borderBottom: `1px solid var(--color-border)`
        }}
      >
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{
            borderColor: 'var(--color-primary)'
          }}></div>
        </div>
      </header>
    );
  }

  // Check if there's any content to show in top bar - all must be explicitly truthy
  const hasTopBarContent = !!(
    phone ||
    email ||
    (socialMedia && socialMedia.length > 0) ||
    breakingNews ||
    (topBarButton?.enabled === true && topBarButton?.text && topBarButton?.url) ||
    (languageButton && languageButton.enabled === true && availableLanguages && availableLanguages.length > 0)
  );

  return (
    <header className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${scrolled ? 'backdrop-blur-md shadow-sm' : ''
      }`} style={{
        borderBottom: `1px solid var(--color-border)`,
        opacity: scrolled ? 1 : 1
      }}>
      <div className="relative">
        {/* Top Bar - with optimized auto-hide */}
        {hasTopBarContent && (
          <div
            className="overflow-hidden"
            style={{
              backgroundColor: 'var(--color-primary)',
              background: `linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)`,
              height: topBarVisible ? 'auto' : '0px',
              opacity: topBarVisible ? 1 : 0,
              paddingTop: topBarVisible ? '4px' : '0px',
              paddingBottom: topBarVisible ? '4px' : '0px',
              transition: 'height 0.2s ease-out, opacity 0.15s ease-out, padding 0.2s ease-out'
            }}
          >
            <div className="container mx-auto px-1 sm:px-4 md:px-6 lg:px-8">
              <div className="flex items-center justify-between">
                {/* Left - Contact Info & Social Links */}
                <div className="flex items-center gap-6">


                  {/* Social Links */}
                  {socialMedia && socialMedia.length > 0 && (
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      {socialMedia.map((social, index) => {
                        const IconComponent = socialIconMap[social.platform];
                        return IconComponent ? (
                          <Button
                            key={social.id || index}
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(social.url, '_blank', 'noopener,noreferrer')}
                            className="transition-all duration-200 text-xs w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full hover:scale-110 p-0"
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'var(--color-primary-foreground)',
                              backdropFilter: 'blur(10px)'
                            }}
                            aria-label={social.platform}
                          >
                            <IconComponent className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
                          </Button>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* Right - Breaking News, TopBar Button & Language */}
                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                 

                  {/* Dynamic TopBar Button from CMS */}
                  {topBarButton?.enabled && topBarButton?.text && topBarButton.url && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (topBarButton.openInNewTab) {
                          window.open(topBarButton.url, '_blank', 'noopener,noreferrer');
                        } else {
                          window.location.href = topBarButton.url;
                        }
                      }}
                      className={`hidden sm:inline-flex items-center border rounded transition-all hover:scale-105 ${topBarButton.size === 'xs' ? '!h-6 px-2 text-[10px] sm:text-xs' :
                          topBarButton.size === 'sm' ? '!h-7 sm:!h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs' :
                            topBarButton.size === 'md' ? '!h-7 sm:!h-8 md:!h-9 px-2 sm:px-3 text-xs sm:text-sm' :
                              topBarButton.size === 'lg' ? '!h-8 sm:!h-9 px-3 sm:px-4 text-xs sm:text-sm' : '!h-7 sm:!h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs'
                        }`}
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'var(--color-primary-foreground)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      {getLabel(topBarButton.text)}
                      {topBarButton.openInNewTab && (
                        <ArrowRight className="ml-0.5 sm:ml-1 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      )}
                    </Button>
                  )}

                  {/* Dynamic Language Selector from CMS */}
                  {languageButton && languageButton.enabled === true && availableLanguages && availableLanguages.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`inline-flex items-center border transition-all hover:scale-105 rounded ${languageButton.size === 'xs' ? '!h-6 px-1.5 sm:px-2 text-[10px] sm:text-xs' :
                              languageButton.size === 'sm' ? '!h-7 sm:!h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs' :
                                languageButton.size === 'md' ? '!h-7 sm:!h-8 md:!h-9 px-2 sm:px-3 text-xs sm:text-sm' :
                                  languageButton.size === 'lg' ? '!h-8 sm:!h-9 px-3 sm:px-4 text-xs sm:text-sm' : '!h-7 sm:!h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs'
                            }`}
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: 'var(--color-primary-foreground)',
                            backdropFilter: 'blur(10px)'
                          }}>
                          <Globe className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                          {languageButton.showFullName ? (
                            <>
                              <span className="hidden md:inline">
                                {availableLanguages.find(l => l.code === language)?.name || 'English'}
                              </span>
                              <span className="md:hidden">
                                {language.toUpperCase()}
                              </span>
                            </>
                          ) : (
                            <span>{language.toUpperCase()}</span>
                          )}
                          <ChevronDown className="ml-0.5 sm:ml-1 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="border-none shadow-sm" style={{
                        backgroundColor: 'var(--color-card)',
                        color: 'var(--color-card-foreground)'
                      }}>
                        {availableLanguages.map((lang) => (
                          <DropdownMenuItem
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            className="transition-all cursor-pointer"
                            style={{
                              backgroundColor: language === lang.code ? 'var(--color-primary)' : 'transparent',
                              color: language === lang.code ? 'var(--color-primary-foreground)' : 'var(--color-card-foreground)'
                            }}
                          >
                            {lang.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Header */}
        <div className={`transition-all duration-300 ${scrolled ? '' : ''}`} style={{
          color: 'var(--color-card-foreground)',
          background: 'var(--color-card)',
          paddingTop: '2px',
          paddingBottom: '2px'
        }}>
          <div className="container mx-auto px-1 ">
            <div className="flex items-center justify-between w-full min-h-[60px]">
              {/* Left - Logo */}
              <Logo
                logoUrl={logo?.url}
                logoAlt={getLabel(logo?.alt)}
                scrolled={scrolled}
              />

              {/* Right - Menu & Join Button */}
              <div className="hidden lg:flex items-center gap-4 xl:gap-6">
                {navigationMenu && navigationMenu.length > 0 && (
                  <div className="relative">
                    <PublicNavigation
                      className={`font-medium transition-all duration-300 ${scrolled ? 'text-sm' : 'text-base'
                        } menu-spacing`}
                      menuItems={navigationMenu}
                    />
                  </div>
                )}
                {joinButton && joinButton.text && joinButton.url && (
                  <Button
                    asChild
                    size={scrolled ? "sm" : "default"}
                    className={`join-button font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-sm ${scrolled ? 'px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm' : 'px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 text-sm sm:text-base'
                      } whitespace-nowrap`}
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-primary-foreground)',
                      border: 'none',
                      // boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <Link href={joinButton.url} className="flex items-center gap-1 sm:gap-2">
                      <span>
                        {getLabel(joinButton.text)}
                      </span>
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </Link>
                  </Button>
                )}
              </div>

              {/* Mobile - Menu Toggle */}
              {navigationMenu && navigationMenu.length > 0 && (
                <button
                  className="lg:hidden h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 flex items-center justify-center border rounded-lg transition-all hover:scale-105"
                  style={{
                    backgroundColor: 'var(--color-muted)',
                    borderColor: 'var(--color-border)'
                  }}
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  {showMobileMenu ? (
                    <X className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'var(--color-foreground)' }} />
                  ) : (
                    <Menu className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'var(--color-foreground)' }} />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && navigationMenu && navigationMenu.length > 0 && (
            <div className="lg:hidden border-t" style={{
              borderColor: 'var(--color-border)',
              backgroundColor: 'var(--color-card)'
            }}>
              <div className="container mx-auto px-1 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-5 md:py-6 space-y-6">
                <div className="relative z-10">
                  <PublicNavigation
                    className="text-base font-medium mobile-nav"
                    isMobile={true}
                    menuItems={navigationMenu}
                  />
                </div>

                {joinButton && joinButton.text && joinButton.url && (
                  <Button
                    asChild
                    className="join-button w-full font-semibold rounded-lg py-2 sm:py-2.5 md:py-3 text-sm sm:text-base transition-all hover:scale-105"
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-primary-foreground)',
                      border: 'none'
                    }}
                  >
                    <Link href={joinButton.url} className="flex items-center justify-center gap-1.5 sm:gap-2">
                      {getLabel(joinButton.text)}
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Link>
                  </Button>
                )}

                {/* Mobile Contact Info */}
                {(phone || email) && (
                  <div className="pt-4 border-t text-center space-y-2" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                      {phone && (
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <Phone className="w-3 h-3" />
                          <span>{phone}</span>
                        </div>
                      )}
                      {email && (
                        <div className="flex items-center justify-center gap-1">
                          <Mail className="w-3 h-3" />
                          <span>{email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </header>

  );
};


export default Header;
